import { CloseCircleGreyIcon } from "assets";
import { ModalPortal, MODAL_LAYER } from "components/modal-portal";
import { useCredits } from "hooks/useCredits";
import { useCreditPricing } from "hooks/useCreditPricing";

interface CreditStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTopupCredit?: () => void;
}

const CircularProgress = ({
  percentage,
  size = 130,
  strokeWidth = 20,
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  const progressColor =
    percentage >= 90 ? "#18B368" : percentage >= 70 ? "#1570EF" : percentage >= 50 ? "#EDA12F" : "#E74444";
  const bgColor = percentage < 50 ? "#FEE2E2" : percentage < 70 ? "#FEF3C7" : "#E5E7EB";

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={bgColor} strokeWidth={strokeWidth} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-3xl font-bold" style={{ color: progressColor }}>
          {percentage}%
        </span>
      </div>
    </div>
  );
};

/**
 * Credit balance detail, opened from the credit indicator in the header/sidebar.
 *
 * The dial measures everything spendable against the account's current ceiling
 * (weekly allowance + purchased), so an account with topped-up credits is never
 * told it is running low. The buckets are listed separately underneath because
 * only the weekly one resets.
 */
const CreditStatusModal = ({ isOpen, onClose, onTopupCredit }: CreditStatusModalProps) => {
  const { credits } = useCredits();
  const { pricing } = useCreditPricing();

  if (!isOpen) return null;

  const weeklyBalance = credits?.weekly.balance ?? 0;
  const weeklyAllowance = credits?.weekly.allowance ?? 0;
  const purchasedBalance = credits?.purchased.balance ?? 0;
  const legacyBalance = credits?.legacy ?? 0;
  const totalAvailable = credits?.totalAvailable ?? 0;
  const ceiling = credits?.spendableCeiling ?? 0;

  const percentage = ceiling > 0 ? Math.round((totalAvailable / ceiling) * 100) : 0;
  const isExhausted = totalAvailable <= 0;

  const { title, subtitle } = isExhausted
    ? {
        title: "Credits Exhausted 😢",
        subtitle:
          "You've used all your available credits. Top up to continue using Precision without interruptions, or wait for your weekly credits to refresh.",
      }
    : percentage <= 25
    ? {
        title: "Running Low on Credits",
        subtitle:
          "You're nearing the end of this week's credits. Top up to keep going without waiting for the weekly refresh.",
      }
    : percentage <= 50
    ? {
        title: "Credit Balance",
        subtitle: "You've used about half this week's credits. Top up to avoid running out mid-workflow.",
      }
    : {
        title: "Credit Balance",
        subtitle: "You have a healthy credit balance. Keep going!",
      };

  return (
    <ModalPortal>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: MODAL_LAYER.gate }}>
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        <div className="relative bg-white rounded-2xl max-w-lg w-full mx-4 overflow-hidden">
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-6 right-4 z-10 hover:opacity-80 transition-opacity"
          >
            <CloseCircleGreyIcon className="w-8 h-8" />
          </button>

          <div className="p-8 pt-6 mt-10">
            <div className="flex justify-center mb-6">
              <CircularProgress percentage={percentage} />
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#012169] mb-3">{title}</h2>
              <p className="text-[#667085]">{subtitle}</p>
            </div>

            <div className="bg-[#F0F5FF] rounded-xl p-4 mb-8 space-y-1.5">
              <p className="flex justify-between text-[#012169]">
                <span>Weekly credits</span>
                <span className="font-bold">
                  {weeklyBalance.toLocaleString()}/{weeklyAllowance.toLocaleString()}
                </span>
              </p>
              <p className="flex justify-between text-[#012169]">
                <span>Purchased credits</span>
                <span className="font-bold">{purchasedBalance.toLocaleString()}</span>
              </p>
              {legacyBalance > 0 && (
                <p className="flex justify-between text-[#012169]">
                  <span>Plan credits</span>
                  <span className="font-bold">{legacyBalance.toLocaleString()}</span>
                </p>
              )}
              <p className="pt-1.5 border-t border-[#C6E0FF] text-sm text-[#475467]">
                Weekly credits are spent first; purchased credits are only used as soon as weekly credits
                run out.
              </p>
            </div>

            <button
              onClick={onTopupCredit}
              className="w-full py-4 rounded-lg font-medium bg-[#012169] text-white hover:bg-[#011557] transition-colors"
            >
              Topup Credit
            </button>
            <p className="text-center text-xs text-[#667085] mt-3">
              Purchased credits stay valid for {pricing.validityDays} days.
            </p>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export { CreditStatusModal };
